import React, { useEffect, useRef } from 'react';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAccept?: () => void;
  title: string;
  children: React.ReactNode;
  acceptText?: string;
  closeText?: string;
  showScrollToTop?: boolean;
  maxWidth?: string;
}

const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  onAccept,
  title,
  children,
  acceptText = 'Accept',
  closeText = 'Close',
  showScrollToTop = true,
  maxWidth = '1000px',
}) => {
  const contentRef = useRef<HTMLDivElement>(null);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  // Scroll to top function
  const scrollToTop = () => {
    if (contentRef.current) {
      contentRef.current.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[9999] p-5 lg:p-4 sm:p-3 animate-fadeIn"
      onClick={onClose}
    >
      <div
        className="bg-[#1a1a1a] rounded-2xl shadow-2xl w-full max-h-[90vh] lg:max-h-[95vh] flex flex-col border border-[#333] animate-slideIn"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth }}
      >
        {/* Modal Header */}
        <div className="flex items-center justify-between px-6 pt-6 pb-4 lg:px-5 lg:pt-5 sm:px-4 sm:pt-4 sm:pb-3 border-b border-[#333] flex-shrink-0">
          <h2 className="text-white text-2xl lg:text-xl sm:text-lg font-semibold m-0">
            {title}
          </h2>
          <button
            className="bg-transparent border-none text-[#999] cursor-pointer p-2 flex items-center justify-center rounded-lg transition-all hover:bg-[#333] hover:text-white"
            onClick={onClose}
            aria-label="Close modal"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path
                d="M18 6L6 18M6 6L18 18"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>

        {/* Modal Body */}
        <div
          className="flex-1 overflow-y-auto px-6 py-6 lg:px-5 lg:py-5 lg:text-[13px] sm:px-4 sm:py-4 sm:text-xs text-[#e0e0e0] text-sm leading-relaxed scrollbar-thin scrollbar-thumb-[#555] scrollbar-track-[#1a1a1a]"
          ref={contentRef}
        >
          {children}
        </div>

        {/* Modal Footer */}
        <div className="px-6 pt-4 pb-6 lg:px-5 lg:pt-4 lg:pb-5 sm:px-4 sm:pt-3 sm:pb-4 border-t border-[#333] flex flex-col gap-4 flex-shrink-0">
          {showScrollToTop && (
            <button
              className="bg-[#333] border-none text-white py-3 px-3 rounded-lg cursor-pointer flex items-center justify-center gap-2 text-sm transition-all hover:bg-[#444] self-center"
              onClick={scrollToTop}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path
                  d="M18 15L12 9L6 15"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Scroll to top
            </button>
          )}

          <div className="flex gap-3 justify-end lg:flex-col">
            <button
              className="py-3 px-8 lg:px-6 lg:py-3.5 rounded-lg text-sm font-medium cursor-pointer border-none transition-all bg-[#333] text-white hover:bg-[#444] lg:w-full"
              onClick={onClose}
            >
              {closeText}
            </button>
            {onAccept && (
              <button
                className="py-3 px-8 lg:px-6 lg:py-3.5 rounded-lg text-sm font-medium cursor-pointer border-none transition-all bg-gradient-to-r from-[#8400ff] to-[#5000a0] text-white hover:from-[#9400ff] hover:to-[#6000b0] lg:w-full"
                onClick={onAccept}
              >
                {acceptText}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Modal;
